<template>
  <HomepageSecondaryLayout>
    <div id="support" class="wrapper">
      <div class="container">
        <div class="row">
          <div id="main-content" class="col-md-9">

            <!-- this block is shown when a topic is selected-->
            <div v-if="topic">
              <ol class="breadcrumb">
                <li class="breadcrumb-item"><router-link to="/support">FAQs</router-link></li>
                <li class="breadcrumb-item active">{{name}}</li>
              </ol>

              <h2 class="topic-header">{{name}}</h2>
              <ul>
                <li v-for="(question, key) in questions" :key="key">
                  <h5 class="faq">{{question.q}}</h5>
                  <p class="answer" v-html="question.a"/>
                  <p class="link" v-html="question.l"/>
                </li>
              </ul>
            </div>

            <!-- this block is shown when no topic or question is selected-->
            <div v-else class="content">
              <ul class="question-container">
                <!-- ul class block-grid -->

                <li class="topic" v-for="(topic, key) in topics" :key="key">
                  <h3>{{topic.name}}</h3>
                  <ul class="question">
                    <li v-for="(question, slug) in topic.questions" :key="slug">
                      <a :href="'/support/'+key+'/'+slug">{{question.q}}</a>
                    </li>

                  </ul>
                  <a :href="'/support/'+key" class="button-small">View all</a>
                </li>
              </ul>

            </div>
          </div>

          <div id="sidebar" class="col-md-3">

            <div class="contact">
              <h3>Contact us</h3>
              <a href="/contact" class="button-medium">Email support</a>
            </div>

             <div class="topics">
                <h3>Help topics</h3>
                  <ul class="help-topics">
                    <li v-for="(topic, key) in topics" :key="key">
                      <router-link :to="{name: 'Support', params: {topic: key}}">{{topic.name}}</router-link>
                    </li>
                  </ul>
             </div>

          </div>
        </div>
      </div>
    </div>

  </HomepageSecondaryLayout>

</template>

<script>
const FAQ = {
  account: {
    name: "Account",
      questions: {
        accountCreate: {
          q: "How do I create an account?",
          a: "Click on the <strong> Sign Up </strong> link in the header to create an account or upload a PDF of your business card and you will \
          be prompted to create an account when you save your design."
        },
        accountBenefits: {
          q: "What are the benefits of creating an account?",
          a: "The main benefits are the ability to save your business card template(s) for future use and the ability to save other business information\
          that expedites business card ordering including team member, shipping, and payment information."
        },
        accountFee: {
          q: "Is there a fee or subscription to create an account?",
          a: "No, there are no fees or subscriptions associated with your online account. We only charge for the print orders you generate through your account."
        },
        accountPayment: {
          q: "How do I pay for my business card orders?",
          a: 'We accept most major credit cards including Visa, Mastercard, American Express, and Discover.'
        },
      }
    },
    artwork: {
      name: "Artwork",
        questions: {
          artworkSize: {
            q: "What size should I make my artwork?",
            a: 'We accept standard sized business card formats in landscape or portrait layouts so \
              your files should measure <strong> 3.5 x 2 inches </strong> (252 pts x 144 pts) or <strong> 2 x 3.5 inches </strong>  (144 pts x 252 pts).\
              If your artwork is on a larger artboard or combined with other other designs, or is in a "multi-up" format and you are not sure how \
              to fix it, please contact us and we will be happy to help you create a file that can be uploaed into our app.'
          },
           artworkSave: {
            q: "How should I save save my artwork file to upload into the Brandly app?",
            a: "Please save it as a <strong> PDF </strong>file."
          },
          artworkText: {
            q: "How does the Brandly app recognize text that I want to make editable?",
            a: 'In order to make text editable in the Brandly app, do <strong>not</strong> convert it to outlines - all "live" text will left editable after uploading into the app.\
                Conversely, if your artwork contains a logo, slogan, or URL with text that never changes, the best practice is to convert that text to outlines.'
          },
          artworkLine: {
            q: "Can I use different fonts, font sizes, and colors in the same line of text?",
            a: 'Yes, you may design a card with two or more fonts, font sizes, or colors on the same line. \
               When you edit your card online, be careful to select a single style at a time to edit otherwise you may erase part\
               of your "line style". If you make a mistake, just click the "redo" button and start over.'
          },
          artworkSecondside: {
            q: "What if my business card has a second side?",
            a: 'You may upload your second side in the app after your front side but if you prefer, our app supports uploading a 2-page PDF file.\
            Just make sure that you create your 2-page PDF file in your native design app (e.g. Illustrator) or Adobe Acrobat rather than a utility like the Mac OS \
            Preview tool - which does not yield a good result with our app.'
          },
          artworkApps: {
            q: "What apps should I use to create my original artwork?",
            a: 'We recommend Adobe InDesign, Illustrator, and Photoshop - all part of the Adobe Creative Cloud suite of professinal design tools. \
                These are the most popular and universal tools that graphic designers use to create layouts, illustrations, and images for their design projects.'
          },
          artworkResolution: {
            q: "What image resolution do you recommend for photos or non-vector graphics?",
            a: 'If you are uploading images (e.g. JPEGs), higher resolutions - around <strong>300 dpi</strong> - work best .'
          },
          artworkColorspace: {
            q: "What colorspace do you recommend?",
            a: 'While there are many color models available today, eventually your file will be converted to<strong> CMYK </strong>(four-color process) for printing purposes so we recommend CMYK.'
          },
          artworkBleed: {
            q: "How do I create a bleed in my artwork?",
            a: 'Bleed is printing that goes beyond the edge of where the sheet will be trimmed - in other words, the area to be trimmed off. \
                You need to create bleeds when printing goes to the edge of a printed piece so that after trimming, you only see the background artwork\
                and not white paper if the trim varies slightly. Watch our <a href="https://www.brandly.com/upload/filepreparation"><span style="text-decoration:underline;">file preparation video</span></a> that explains how to create a bleed in your artwork.'
          },
        }
      },
     browserSupport: {
      name: "Browsers",
        questions: {
          browsersSupported: {
            q: "What browsers work with the Brandly app?",
            a: 'The Brandly app is designed to operate on modern browsers. We recommend our users to use the following supported browsers:\
              <ul style = "width:170px;line-height:2;">\
                <li style="margin-left:1.5em;"> \
                <a href="https://www.google.com/chrome"><span style="text-decoration:underline;">Google Chrome</span></a>\
                <img style = "float:right" src="/static/images/support/chrome.png" height="24" width="24"></li>\
                <li style="margin-left:1.5em;"> \
                <a href="https://www.mozilla.org/en-US"><span style="text-decoration:underline;">Mozilla Firefox</span></a>\
                <img style = "float:right" src="/static/images/support/firefox.png" height="24" width="24"></li>\
                <li style=" display: none; /*TODO coming soon*/ margin-left:1.5em;"> \
                <a href="https://www.apple.com/safari"><span style="text-decoration:underline;">Apple Safari</span></a>\
                <img style = "float:right" src="/static/images/support/safari.png" height="24" width="24"></li>\
                <li style=" display: none; /*TODO coming soon*/ margin-left:1.5em;"> \
                <a href="https://www.microsoft.com/en-us/windows/microsoft-edge"><span style="text-decoration:underline;">Microsoft Edge</span></a>\
                <img style = "float:right" src="/static/images/support/edge.png" height="24" width="24"></li>\
              </ul>'
          },
          otherBrowsers: {
            q: "What about other browsers?",
            a: "Browsers other than those present in the list of recommended web browsers may have compatibility issues with the Brandly App and we recommend users to switch\
               to a browser in the list if they are using one that is not present here."
          },
          olderBrowsers: {
            q: "What about older versions of approved browsers?",
            a: 'Most modern web browsers will automatically update themselves so that you are always running the most current version. You no longer need to actually\
                "download and install" the latest version yourself; it will do it for you.'
          },
        }
    },
    businesscardTemplates: {
      name: "Business Card Templates",
      questions: {
        squareCorners: {
          q: "Standard Size Business Cards",
          a: 'To begin creating your (US) Standard Size Business Cards designs, please download a blank template.',
          l:  ' <a href="/static/downloads/blank-ai-templates.zip">Adobe Illustrator Template</a>'
        },
      }
    },
    fileupload: {
      name: "File Upload",
        questions: {
          fileuploadPDF: {
            q: "Why do you require a PDF upload to create my business card template?",
            a: "Everything we need is in the PDF, including embedded fonts and graphics which means fewer issues. PDF is the print industry standard so\
              from one provider to the next, they allow a consistent and unchanging view of what the file should look like. <strong>Note:</strong> while\
              we require a PDF to generate your online business card template, you may upload image files within the Brandly app if your business card contains photos."
          },
          fileuploadFile: {
            q: "Are there any restrictions on the file size I upload?",
            a: "We limit the size of uploaded files to 5 megabytes. The physical size of the layout should be standard landscape or portrait size for\
            a US business card - or 3.5 x 2 inches or 2 x 3.5 inches plus bleed if necessary. Learn more about file dimensions in the Artwork section of our support docs."
          },
          fileuploadScan: {
            q: "Can I upload a scan of my card saved as a PDF?",
            a: "<b>Scanned images</b> or photos of your business card saved as PDFs <b>do not work</b>. Scans and photos, are made up of pixels - tiny squares of\
               individual color - that make up what you see on your screen, but do not contain the necessary embedded graphic information - fonts, logos, images\
               - to make high quality graphic reproductions."
          },
          fileuploadOriginal: {
            q: "What happens to my original artwork file on upload?",
            a: 'Your PDF file is convered into a 2 layer file - an editable "type layer" in <a href="https://en.wikipedia.org/wiki/Scalable_Vector_Graphics"> \
            <span style="text-decoration:underline;">SVG format</span></a> and a background layer which consists of any static background artwork \
            (e.g. images and logos). Your original file and the converted file are saved in the cloud for future use.'

          },
           fileuploadProblem: {
            q: "What should I do if there is a problem during my upload?",
            a: 'If you see something in the proof that looks wrong or if your file fails to upload, \
                please contact us and we will look into the the underlying probem.'
          },
          fileCommmonproblems: {
            q: "What are the most common problems you encounter with file upload?",
            a: 'Here is a list of issues that frequently cause problems:\
              <ul>\
                <li style="margin-left:1.5em;">incorrect document (trim) size</li>\
                <li style="margin-left:1.5em;">missing bleeds (extending artwork 1/8" beyond trim size) </li>\
                <li style="margin-left:1.5em;">leaving trim marks in the file. Files need only contain artwork that prints. Leave off extraneous marks. </li>\
                <li style="margin-left:1.5em;">missing fonts - we do not have your fonts. Please contact us to resolve.</li>\
                <li style="margin-left:1.5em;">garbled text/fonts - often associated with 2-page PDFs that are created using MacOS Preview utility</li>\
                \
              </ul>'
          },
        }
      },
    fonts: {
      name: "Fonts",
        questions: {
          fontInclude: {
            q: "How do I include fonts in my business card PDF for you to match?",
            a: "If you save your file <strong>without</strong> converting the text to outlines, you include fonts in your file thanks to the Adobe PDF format. \
            When you convert fonts to outlines in Adobe Illustrator or InDesign you erase all of the embedded font information and render the text uneditable to service providers. \
            If you want to be able to make a file editable in the Brandly application, do <b>not</b> convert fonts to outlines."
          },
          fontMatch: {
            q: "How does Brandly match the fonts in my PDF?",
            a: "We extract the font information from your uploaded PDF and use algorithms to compare it to our library of fonts from which\
            we find matches."
          },
          fontWhy: {
            q: "Why does Brandly need to match the fonts in my PDF?",
            a: "The Adobe PDF format only stores the font information necessary to support the characters used in a given file. In order to make the file editable; to create any letter,\
            number, or character in a given font, we need to have the matching font resident in our computer systems."
          },
          fontMissing: {
            q: "What if you don't have my font?",
            a: 'If we do not have your font, you will encounter a "font missing" error upon uploading your PDF. If this happens, please contact us and we will look into obtainibng\
            the missing font.'
          },
          fontScramble: {
            q: "Why do my letters look scrambled?",
            a: 'We have emcountered some uploads that contain garbled or scrambled text and have tracked it down to files edited in the MacOS Preview utility. If you encounter\
            scrambled text, please send up your artwork files and we will resave them for you.'
          },
        }
      },
    orders: {
      name: "Orders",
        questions: {
          ordersTeam: {
            q: "How do I place an order for myself?",
            a: 'Log in and find your design template and edit the fields as necessary, add to your cart and checkout. Ordering usually takes about a minute.'
          },

          ordersTeam: {
            q: "What is the best way to order for large teams?",
            a: 'If you are an account admin, go to the "teams" tab on your dashboard and invite team members to self-order. When you send a team member an invite and they\
            accept, they need only imput a few missing pieces of information and they will be presented with a final proof and then they can check out.'
          },
          ordersReorder: {
            q: "How do I place reorders?",
            a: "There are a few ways to place a reorder - log into your account and go to the orders tab and find the order you wish to reorder and\
            click submit. Alternatively, you may search for a specific order ID in your email and click on the reorder link in your original order\
            confirmation."
          },
          ordersChange: {
            q: "Can I request a change after I approve my proof?",
            a: "If you catch a mistake after editing and proofing your card you may either hit the back button in your browser to navigate to\
            the page where you edited your card information or you may drop the item from your cart at checkout."
          },
          ordersCharge: {
            q: "When do you charge my credit card?",
            a: "Your card is charged when your order is submitted into production."
          },
        }
      },
    paper: {
      name: "Paper",
        questions: {
          paperTypes: {
            q: "What types of papers and substrates do you print on?",
            a: 'We print on eighteen different papers and substrates from 11pt to 34pt in thickness. We have coated and uncoated papers, bright white paper, \
              linen and vellum textured papers, UV gloss and Soft-touch finishes, double-thick and Color Core card stocks, as well as plastic, and pearlescent subtrates. \
              There is a great choice for every design and every use case. Click on the "Business Cards" link in the header to learn more about our papers.'
          },
           paperPopular: {
            q: "What are your most popular papers?",
            a: "Standard Uncoated is a popular choice and a great value. It's clean, super-white, and it's easy to write on with pen or pencil. We also find a lot of customers gravitate to matte coated papers\
             in both Standard and Premium weights because they're versatile - great for fine print and saturated color."
          },
          paperHeavyink: {
            q: "What are your best papers for heavy ink coverage?",
            a: 'Coated papers are the best option for heavy ink coverage. We carry coated papers in both Standard 14 pt. and Premium 16 pt. thicknesses. UV Coating is a  \
            great option for photo cards or edge-to-edge ink coverage.'
          },
          paperThickness: {
            q: "How thick are your thickest papers?",
            a: "We sell a Double Thick Ultrawhite card that weighs in at 34 pt. That's over twice as thick as our 16 pt. Premium papers so plenty thick for anyone looking for that type of product. \
            The Color Core papers are all 32 pt. so they're also double the thickness of most Premium papers and available with 5 different seam colors that are visible along the edge of each card."
          },
           paperSoftTouch: {
            q: "What is Soft Touch?",
            a: 'SofTouch is actually a coating applied to the front side of your cards that makes them feel soft to the touch. The texture is often described as  \
            "velvety" or "refined" - a tactile experience that surprises many of our customers. For best results and a superior tactile experience, we recommend \
            choosing Soft Touch for projects with light coverage artwork, where the design background is "mostly white".'
          },
           paperCustom: {
            q: "Do you print on other papers or substrates not listed on the website?",
            a: 'We have pared our paper options down to the ones most customers care about but if you have a special request, plese contact us and we will let you know if we can\
            find it.'
          },
        }
      },
      printing: {
      name: "Printing",
        questions: {
          printingSize: {
            q: "What size are the business cards you print?",
            a: 'Brandly currently offers Standard US sized cards:\
              <ul>\
                <li style="margin-left:1.5em;">3.5 x 2 inches landscape</li>\
                <li style="margin-left:1.5em;">2 x 3.5 inches portrait</li>\
                \
              </ul>'
          },
           printingFullcolor: {
            q: "Do you print in full color?",
            a: 'Yes, we print full color on one or both sides. Full color printing, also called four-color process printing, combines four "additive" colors -\
               cyan, yellow, magenta, and black - to create the full gamut of colors.'
          },
          printingBothsides: {
            q: "Do you print on both sides?",
            a: "Yes, we print full color on one or both sides. Be sure to follow our instructions in the Artwork and File Upload sections of this FAQ on how to best\
            prepare and upload files for 2 sided business cards."
          },
           printingBothsidecosts: {
            q: "Is there an extra charge for printing on the second side of my business cards?",
            a: "No, second side printing is<strong> FREE </strong>on all business cards."
          },
          printingRaised: {
            q: "Do you offer raised printing?",
            a: 'We offer <a href="https://en.wikipedia.org/wiki/Thermographic_printing"><span style="text-decoration:underline;">thermography</span></a> which is a process where plastic is applied to the printed page and heated causing\
               the printed image to "rise" resulting in raised printing.'
          },
          printingFinishing: {
            q: "What finishing options do you offer?",
            a: 'We offer rounded corners - adding a radius to all 4 corners of your cards - on most paper stocks. Go to the "Business Cards" link\
            in the header to learn more.'
          },
        }
      },
      shipping: {
      name: "Shipping",
        questions: {
          shippingCost: {
            q: "How much does shipping cost?",
            a: "Shipping is <strong>FREE</strong> on all orders. Really."
          },
          shippingHow: {
            q: "How do orders ship?",
            a: "All orders are shipped UPS Ground."
          },
          shippingArrive: {
            q: "How long does it take to print and ship my order?",
            a: "Your business cards will be <strong>printed in 3 business days</strong> after the day you place your order and UPS Ground will add <strong> 3 - 5 shipping days</strong>.\
               Note: Brandly's business days are Monday to Friday, excluding major US holidays. "
          },
          shippingTrack: {
            q: "Can I track my order?",
            a: "We email a tracking link to you when we ship your order."
          },

        }
      },
      support: {
      name: "Support",
        questions: {
          supportGeneral: {
            q: "What is the best way to find out more about the application or your products?",
            a: 'We prefer that you use our chat, email, or our <a href="https://www.brandly.com/contact"><span style="text-decoration:underline;">contact form</span></a> as a first step.'
          },
           supportUpload: {
            q: "I'm having trouble uploading a PDF of my business card.",
            a: 'Click the suggested links on the upload page or go directly to the Artwork and File Upload sectons of this FAQ. Contact us if the issue persists.'
          },

          supportLogin: {
            q: "I'm having trouble signing in.",
            a: 'Try the "Forgot password" link on the Sign-In page first. Contact us if the issue persists.'
          },
          supportQuote: {
            q: "Can I get a quote?",
            a: 'If you need a custom quote on a project, please contact us.'
          },
          supportRep: {
            q: "Can I speak to a support representative?",
            a: 'Leave us your number and we will call you back.'
          },
        }
      }
};
export default {
  props: ["topic", "question"],
  computed: {
    topics() {
      return FAQ;
    },
    name() {
      return FAQ[this.topic].name;
    },
    questions() {
      return FAQ[this.topic].questions;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~scss/colors.scss';


/* general */

a {
  font-weight: 600;
  }

ul {
  display: block;
  list-style: none;
  margin-bottom: 1em;
}

li {
  display: list-item;
  text-align: left;
  line-height: 1.6;
}

/* grid */

.grid {
  margin-left: -10px;
  margin-right: -10px;
  min-height: 1000px;
}

.block-grid {
  display: block;
  list-style: none;
  padding: 0;
  overflow: hidden;
  margin: 0 -10px;
  float: left;
}

/* classes */

.content {
  width: 100%;
}

.wrapper {
  margin: 1em auto;
  width: 100%;
  max-width: 1000px;
  min-height: calc(100vh - 200px);
}

ul.question{
  height:85px;
  overflow: hidden;
}

.question-container {
  display: flex;
  flex-wrap: wrap;
}

.topic {
  width: 50%;
  padding: 1em;
}

.block-grid ul{
  max-height:85px;
  overflow: hidden;
}

.breadcrumb {
  margin: 0.75em 0 2em 0;
  padding: 6px 10px;
  background: rgba(128,128,128,.2);
  color: black;
  font-weight: 700;
}

/* Questions ASKED */

.question a:hover {
  text-decoration: underline;
  }

.topic li {
  list-style: none;
  font-size: 0.75em;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.button-small {
  display: inline-block;
  padding: 4px 12px;
  background-color:$red;
  color: white;
  font-size:.85em;
  font-weight: 400;
  text-align: center;
  vertical-align: middle;
  border-radius: 2px;
  cursor: pointer;
}

/* Questions ANSWERED */

.topic-header{
  text-align: left;
  margin-bottom: 1em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.faq {
  // color: $black-0;
  text-align: left;
  margin-bottom:.5em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.answer {
  font-size: .85em;
  text-align: left;
  margin-bottom:1.5em;
}

.link {
  font-size: .85em;
  margin-left:1.5em;

  &:hover{
    text-decoration: underline;
  }
}

/* Sidebar */

.contact h3 {
  padding: .5em 0;
}

.button-medium {
  background-color:$red;
  color: white;
  font-size: 1.2rem;
  padding: 6px 12px;
  border-radius: 2px;

}

.topics h3 {
  margin-top: 2em;
}

.topics{
  margin-top: 1em;
}

.help-topics li{
  font-size:.85em;
  line-height: 2;
}

.help-topics a:hover,
.help-topics a:active {
    color:$red;
  }

  @media only screen and (max-width: 414px) {
  h3{
    font-size:.85em;
     white-space: nowrap;
     overflow: hidden;
     text-overflow: ellipsis;
    }
  }

</style>
<style lang="scss">
@import '~scss/colors.scss';

// Unscoped -- target the HTML inside the JSON
// This is needed because v-html + scoped doesn't work
// as expected and neither does >>> :<
p.link a {
  color: $red;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/homepage/Support.vue